import { Button, Grid, Link } from '@material-ui/core';
import { MUIDataTableColumn, MUIDataTableMeta } from 'mui-datatables';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PeriodDTO } from '../../models/dto/PeriodDTO';
import { getMedicalCertificateImageData } from '../../services/peopleManagement.service';
import { AppState } from '../../store';
import { isAuthenticatedFromGDP } from '../../store/actions/authActions';
import { getMedicalCertificates } from '../../store/actions/medicalCertificatecActions';
import { CondominiumReducerData } from '../../store/reducers/condominiumReducer';
import { EmployeesReducerData } from '../../store/reducers/employeesReducer';
import { MedicalCertificatesReducerData } from '../../store/reducers/medicalCertificatesReducer';
import CustomDataTable from '../CustomDataTable';
import ImagePreviewDialog, { ImageData } from '../ImagePreviewDialog';
import SelectPeriod from '../SelectPeriod';
import TableSkeleton from '../TableSkeleton';

const MedicalCertificatesTable: React.FC = () => {
  const dispatch = useDispatch();
  const {
    medicalCertificates,
    loadingMedicalCertificates,
  }: MedicalCertificatesReducerData = useSelector(
    (state: AppState) => state.medicalCertificatesReducer,
  );

  const { employees }: EmployeesReducerData = useSelector(
    (state: AppState) => state.employeesReducer,
  );

  const [periodSelected, setPeriodSelected] = useState<PeriodDTO | null>(null);
  const [loadingImage, setLoadingImage] = useState<boolean>(false);
  // const [
  //   clickedViewFromAllReferences,
  //   setClickedViewFromAllReferences,
  // ] = useState<boolean>(false);
  const [openImagePreviewDialog, setOpenImagePreviewDialog] = useState<boolean>(
    false,
  );
  const [imagePreviewDialogTitle, setImagePreviewDialogTitle] = useState('');
  const [
    medicalCertficateImageSelected,
    setMedicalCertficateImageSelected,
  ] = useState<ImageData | undefined>();

  const { condominiumSelected }: CondominiumReducerData = useSelector(
    (state: AppState) => state.condominiumReducer,
  );

  const getMedicalCertificateImage = async (fileHash: string) => {
    setLoadingImage(true);
    try {
      const data = await getMedicalCertificateImageData(fileHash);

      setMedicalCertficateImageSelected({
        contentType: data.contentType,
        fileBuffer: data.fileBuffer,
      });
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingImage(false);
    }
  };

  const getMedicalCertificatesFromAllReferences = () => {
    if (periodSelected) {
      dispatch(
        getMedicalCertificates({
          periodFrom: periodSelected.inicioMesApuracao,
          periodUntil: periodSelected.fimMesApuracao,
        }),
      );
    }
  };

  // const handleViewFromAllReferences = () => {
  //   setClickedViewFromAllReferences(true);
  //   getMedicalCertificatesFromAllReferences();
  // };

  const handleOpenImagePreview = (
    fileHash: string,
    employeeName: string,
    justificationData: string,
  ) => {
    setOpenImagePreviewDialog(true);
    setImagePreviewDialogTitle(`${justificationData} - ${employeeName}`);
    getMedicalCertificateImage(fileHash);
  };

  useEffect(() => {
    if (periodSelected && condominiumSelected) {
      // setClickedViewFromAllReferences(false);
      dispatch(
        getMedicalCertificates({
          reference: condominiumSelected.referencia,
          periodFrom: periodSelected.inicioMesApuracao,
          periodUntil: periodSelected.fimMesApuracao,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodSelected, condominiumSelected]);

  const columns: MUIDataTableColumn[] = [
    {
      name: 'dataCriado',
      label: 'Data de Envio',
    },
    {
      name: 'dataJustificativa',
      label: 'Data da Justificativa',
    },
    // {
    //   name: 'referencia',
    //   label: 'Referência do Condomínio',
    //   options: {
    //     // display: clickedViewFromAllReferences,
    //     customBodyRender: (value: any, _tableMeta: any, _updateValue: any) =>
    //       employees.find(item => {
    //         return item.numeroCadastro === Number(value);
    //       })?.nome || value,
    //   },
    // },
    {
      name: 'criadoPor',
      label: 'Colaborador',
      options: {
        customBodyRender: (value: any, _tableMeta: any, _updateValue: any) =>
          employees.find(item => {
            return item.numeroCadastro === Number(value);
          })?.nome || value,
      },
    },
    {
      name: 'hashArquivo',
      label: 'Atestado',
      options: {
        customBodyRender: (
          value: any,
          tableMeta: MUIDataTableMeta,
          _updateValue: any,
        ) => {
          const employeeName =
            employees.find(item => {
              return item.numeroCadastro === Number(tableMeta.rowData[2]);
            })?.nome || value;

          const justificationDate = tableMeta.rowData[1];
          return (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <Link
              component="button"
              onClick={() =>
                handleOpenImagePreview(value, employeeName, justificationDate)
              }
              color="primary"
            >
              Visualizar Atestado
            </Link>
          );
        },
      },
    },
  ];

  return (
    <>
      {openImagePreviewDialog && (
        <ImagePreviewDialog
          open={openImagePreviewDialog}
          title={imagePreviewDialogTitle}
          loading={loadingImage}
          image={medicalCertficateImageSelected}
          handleClose={() => setOpenImagePreviewDialog(false)}
        />
      )}

      <Grid container direction="column" justify="space-between">
        <Grid item xs={12}>
          <Grid container justify="space-between">
            <Grid item>
              <SelectPeriod
                showLabel
                disableUnderline
                value={periodSelected}
                handlePeriodChange={setPeriodSelected}
              />
            </Grid>
            {!isAuthenticatedFromGDP() && (
              <Grid item>
                <Button
                  onClick={getMedicalCertificatesFromAllReferences}
                  variant="outlined"
                  color="default"
                >
                  Visualizar de todos os condomínios
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {loadingMedicalCertificates ? (
            <TableSkeleton rows={4} />
          ) : (
            <CustomDataTable
              data={medicalCertificates.sort((a, b) =>
                a.dataCriado < b.dataCriado ? 1 : -1,
              )}
              columns={columns}
              options={{
                elevation: 0,
              }}
              download={!isAuthenticatedFromGDP()}
              title=""
            />
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default MedicalCertificatesTable;
